import { BaseSyntheticEvent, useMemo, useState } from "react";

export type InputRangeProps = {
  min: number;
  max: number;
  step?: number;
  leftBound?: number;
  setLeftBound?: (value: number) => void;
  rightBound?: number;
  setRightBound?: (value: number) => void;
};

type Direction = "right" | "left";

export const InputRange: React.FC<InputRangeProps> = ({
  leftBound,
  setLeftBound,
  rightBound,
  setRightBound,
  min,
  max,
  step = 1,
}) => {
  const circleClassNameSmall =
    "absolute top-[16px] h-2 w-2 -left-1 rounded-full shadow-md bg-primary-200 transition-all";
  const circleClassNameBig =
    "absolute top-[10px] h-5 w-5 -left-2.5 rounded-full shadow-md bg-primary-200 transition-all";
  const lineClassName = "absolute top-[19px] h-0.5";
  const mainLineClassName = [lineClassName, "w-full", "bg-neutral-200"].join(" ");
  const highlightedLineClassName = [lineClassName, "bg-primary-200"].join(" ");

  const [direction, setDirection] = useState<Direction>("right");
  const [isActive, setIsActive] = useState(false);

  const circleClassName = isActive ? circleClassNameBig : circleClassNameSmall;

  const ignoreEvent = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const { hasOnlyRightBound, hasOnlyLeftBound, hasBothBounds, hasLeftBound, hasRightBound } = useMemo(() => {
    const hasLeftBound = typeof leftBound !== "undefined" && typeof setLeftBound !== "undefined";
    const hasRightBound = typeof rightBound !== "undefined" && typeof setRightBound !== "undefined";

    return {
      hasLeftBound,
      hasRightBound,
      hasBothBounds: hasLeftBound && hasRightBound,
      hasOnlyRightBound: hasRightBound && !hasLeftBound,
      hasOnlyLeftBound: hasLeftBound && !hasRightBound,
    };
  }, [leftBound, setLeftBound, rightBound, setRightBound]);

  const onPointerLeave = () => {
    setIsActive(false);
  };

  const onPointerOver = () => {
    setIsActive(true);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);

    if (hasOnlyLeftBound) {
      setLeftBound?.(value);
    }

    if (hasOnlyRightBound) {
      setRightBound?.(value);
    }

    if (typeof rightBound === "number" && typeof leftBound === "number") {
      const middle = (rightBound + leftBound) / 2;

      switch (true) {
        case value > middle: {
          setRightBound?.(Math.max(value, leftBound + step));
          setDirection("left");
          break;
        }
        case value < middle: {
          setLeftBound?.(Math.min(value, rightBound - step));
          setDirection("right");
          break;
        }

        case rightBound - leftBound === 2 * step && direction === "right": {
          setLeftBound?.(Math.min(value, rightBound - step));
          setDirection("right");
          break;
        }

        case rightBound - leftBound === 2 * step && direction === "left": {
          setLeftBound?.(value - step);
          setRightBound?.(value);
          break;
        }

        default:
          break;
      }
    }
  };

  // Proportion
  // max - min : 100 = rightBound - min : rightBoundPercentage
  const rightBoundPercentage = useMemo(() => {
    if (typeof rightBound === "undefined") return 0;
    if (max <= min) return 0;

    return (100 * (rightBound - min)) / (max - min);
  }, [min, max, rightBound]);

  const leftBoundPercentage = useMemo(() => {
    if (typeof leftBound === "undefined") return 0;
    if (max <= min) return 0;

    return (100 * (leftBound - min)) / (max - min);
  }, [min, max, leftBound]);

  return (
    <div className="relative h-10 px-[10px]" onPointerOver={onPointerOver} onPointerLeave={onPointerLeave}>
      <input
        type="range"
        className="absolute w-full opacity-0 top-3"
        min={min}
        max={max}
        step={step}
        value={leftBound}
        onChange={onChange}
        onKeyDown={ignoreEvent}
      />

      <div className="relative h-10 pointer-events-none">
        <div className={mainLineClassName} />
        {hasOnlyRightBound && (
          <div
            className={highlightedLineClassName}
            style={{
              width: `${rightBoundPercentage}%`,
            }}
          />
        )}

        {hasOnlyLeftBound && (
          <div
            className={highlightedLineClassName}
            style={{
              left: `${leftBoundPercentage}%`,
              width: `${100 - leftBoundPercentage}%`,
            }}
          />
        )}

        {hasBothBounds && (
          <div
            className={highlightedLineClassName}
            style={{
              left: `${leftBoundPercentage}%`,
              width: `${rightBoundPercentage - leftBoundPercentage}%`,
            }}
          />
        )}

        {hasRightBound && (
          <div
            className="absolute"
            style={{
              left: `${rightBoundPercentage}%`,
            }}
          >
            <div className={circleClassName} />
          </div>
        )}

        {hasLeftBound && (
          <div
            className="absolute"
            style={{
              left: `${leftBoundPercentage}%`,
            }}
          >
            <div className={circleClassName} />
          </div>
        )}
      </div>
    </div>
  );
};
